import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { ThemeSwitcher } from "sj-ui-toolkit/src/components/ThemeSwitcher/ThemeSwitcher";
import { CreatableMultiSelect } from "sj-ui-toolkit/src/components/CreatableMultiSelect/CreatableMultiSelect";
import { CreatableSelect } from "sj-ui-toolkit/src/components/CreatableSelect/CreatableSelect";
import { AiStackSelector } from "./AiStackSelector";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import type { DevProfileFormData } from "./DeveloperProfilePage";

// creatable fields let users type their own entry, so the "Other" options
// from the shared lists are dropped here
const languageOptions = DeveloperProfileConstants.OPTIONS.programmingLanguages.filter(
  option => option.value !== DeveloperProfileConstants.VALUES.OTHER
);
const toolingOptions = DeveloperProfileConstants.OPTIONS.architectureTools.filter(
  option => option.value !== DeveloperProfileConstants.VALUES.OTHER
);

interface DeveloperProfileFormProps {
  formData: DevProfileFormData;
  handleChange: (field: string, value: unknown) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
  shouldDisableCodeFields: boolean;
}

export function DeveloperProfileForm({
  formData,
  handleChange,
  handleSubmit,
  loading,
  shouldDisableCodeFields,
}: DeveloperProfileFormProps) {
  const [step, setStep] = useState(1);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isFirstRender = useRef(true);

  // move focus to the heading when the screen changes so it's announced
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [step]);

  const dropdownProps = (id: string, placeholder: string) => ({
    inputId: id,
    placeholder,
    className: "w-full",
  });

  const dropdownPt = (placeholder: string) => ({
    pt: { select: { "aria-label": placeholder } },
  });

  const hasAiSelection =
    formData.doesNotUseAi ||
    Object.values(formData.aiStack).some(selections => selections.length > 0);

  // screen two only shows the sections the user's screen-one answers call for;
  // when neither applies they finish on the first screen
  const showTechStack = !shouldDisableCodeFields;
  const showAiStack = !formData.doesNotUseAi;
  const canFinishOnStepOne = !showTechStack && !showAiStack;

  const handleStepOneSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (canFinishOnStepOne) {
      handleSubmit(e);
    } else {
      setStep(2);
    }
  };

  const goBackToStepOne = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep(1);
  };

  return (
    <form
      onSubmit={step === 1 ? handleStepOneSubmit : handleSubmit}
      data-testid="developer-profile-form"
    >
      {step === 1 ? (
        <section className="flex flex-col gap-6 mb-8 max-w-xl">
          <h2 ref={stepHeadingRef} tabIndex={-1} className="atlas-h3">
            First, let's personalize your experience.
          </h2>
          <div className="flex flex-col gap-2">
            <p className="body-md">
              Which appearance for the platform do you prefer? (You can change this later in
              settings)
            </p>
            <ThemeSwitcher />
          </div>
          <div className="flex flex-col gap-2">
            <label className="body-md" htmlFor="jobFunction">
              What best describes your job function with your organization?
            </label>
            <Dropdown
              {...dropdownProps("jobFunction", "Select job function")}
              {...dropdownPt("Select job function")}
              data-testid="developer-profile-job-function-dropdown"
              value={formData.jobFunction}
              options={DeveloperProfileConstants.OPTIONS.jobRoles}
              onChange={e => handleChange("jobFunction", e.value)}
              required
            />
          </div>
          {formData.jobFunction === DeveloperProfileConstants.VALUES.OTHER && (
            <div className="flex flex-col gap-2">
              <label className="body-md" htmlFor="jobFunctionOther">
                Please specify job function
              </label>
              <InputText
                id="jobFunctionOther"
                data-testid="developer-profile-job-function-other-input"
                value={formData.jobFunctionOther}
                onChange={e => handleChange("jobFunctionOther", e.target.value)}
                placeholder="Enter your job function"
                className="w-full"
                required
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <label className="body-md" htmlFor="expertise">
              Which of the following best describes your technical expertise?
            </label>
            <Dropdown
              {...dropdownProps("expertise", "Select expertise")}
              data-testid="developer-profile-expertise-dropdown"
              value={formData.expertise}
              options={DeveloperProfileConstants.OPTIONS.technicalExpertise}
              onChange={e => handleChange("expertise", e.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="body-md">Do you use any AI tools in your day-to-day work?</p>
            <div className="flex items-center gap-2">
              <Checkbox
                inputId="doesNotUseAi"
                data-testid="developer-profile-does-not-use-ai-checkbox"
                checked={formData.doesNotUseAi}
                onChange={e => handleChange("doesNotUseAi", e.checked)}
              />
              <label className="body-md" htmlFor="doesNotUseAi">
                I don't use AI tools
              </label>
            </div>
          </div>
        </section>
      ) : (
        <section
          className={
            showTechStack && showAiStack
              ? "grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-6 mb-6"
              : "flex flex-col gap-4 mb-6 max-w-xl"
          }
        >
          {showTechStack && (
            <div className="flex flex-col gap-4">
              <h2 ref={stepHeadingRef} tabIndex={-1} className="atlas-h3">
                Tech Stack
              </h2>
              <div className="flex flex-col gap-2">
                <label className="body-md" htmlFor="experience">
                  How many years of development experience do you have?
                </label>
                <Dropdown
                  {...dropdownProps("experience", "Select experience level")}
                  {...dropdownPt("Select experience level")}
                  data-testid="developer-profile-experience-dropdown"
                  value={formData.experience}
                  options={DeveloperProfileConstants.OPTIONS.experienceLevels}
                  onChange={e => handleChange("experience", e.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="body-md" htmlFor="language">
                  What is your primary programming language?
                </label>
                <CreatableSelect
                  {...dropdownProps("language", "Select language")}
                  data-testid="developer-profile-language-select"
                  value={formData.language}
                  options={languageOptions}
                  onChange={e => handleChange("language", e.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="body-md" htmlFor="additionalLanguages">
                  Select other programming languages you use (optional):
                </label>
                <CreatableMultiSelect
                  {...dropdownProps("additionalLanguages", "Select language(s)")}
                  data-testid="developer-profile-additional-languages-select"
                  value={formData.additionalLanguages}
                  options={DeveloperProfileConstants.OPTIONS.additionalLanguages}
                  onChange={e => handleChange("additionalLanguages", e.value)}
                  display="chip"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="body-md" htmlFor="tooling">
                  Select the tools you use:
                </label>
                <CreatableMultiSelect
                  {...dropdownProps("tooling", "Select tool(s)")}
                  data-testid="developer-profile-tooling-select"
                  value={formData.tooling}
                  options={toolingOptions}
                  onChange={e => handleChange("tooling", e.value)}
                  display="chip"
                />
              </div>
            </div>
          )}
          {showAiStack && (
            <div className="flex flex-col gap-4">
              <h2
                ref={showTechStack ? undefined : stepHeadingRef}
                tabIndex={-1}
                className="atlas-h3"
              >
                AI Stack
              </h2>
              <AiStackSelector
                value={formData.aiStack}
                onChange={aiStack => handleChange("aiStack", aiStack)}
              />
            </div>
          )}
        </section>
      )}
      <div className="flex justify-end gap-3">
        {step === 1 ? (
          // distinct keys keep React from reusing a button's DOM node across
          // the step swap, which would let an in-flight click submit the form
          <Button
            key="step-one-submit"
            type="submit"
            data-testid="developer-profile-step-one-submit-button"
            label={canFinishOnStepOne ? "Let's Go!" : "Next"}
            loading={loading}
            disabled={loading}
          />
        ) : (
          <>
            <Button
              key="back"
              type="button"
              data-testid="developer-profile-back-button"
              label="Back"
              outlined
              severity="secondary"
              onClick={goBackToStepOne}
            />
            <Button
              key="step-two-submit"
              type="submit"
              data-testid="developer-profile-step-two-submit-button"
              label="Let's Go!"
              loading={loading}
              disabled={loading || !hasAiSelection}
            />
          </>
        )}
      </div>
    </form>
  );
}
